import { useMemo, useState } from "react"

import { useTranslation } from "react-i18next"

import type { StyleTemplate } from "@/features/studio-ai/types/studio-ai.types"

import { ArrowLeft, Plus } from "@/components/icons"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Spinner } from "@/components/ui/spinner"
import {
  getStyleTemplateErrorMessage,
  useDeleteStyleTemplate,
  useStyleTemplates,
} from "@/features/studio-ai/hooks/use-style-templates"

import { StyleTemplateCard } from "./style-template-card"

type StyleTemplateManagerProps = {
  onBack?: () => void
  onCreate: () => void
  onEdit: (template: StyleTemplate) => void
}

export function StyleTemplateManager({ onBack, onCreate, onEdit }: StyleTemplateManagerProps) {
  const { t } = useTranslation(undefined, { keyPrefix: "studioAi.styleTemplates" })
  const { data: templates = [], isLoading, isError, error, refetch } = useStyleTemplates()
  const { mutateAsync: deleteTemplate, isPending: isDeleting } = useDeleteStyleTemplate()
  const [search, setSearch] = useState("")
  const [templateToDelete, setTemplateToDelete] = useState<StyleTemplate | null>(null)

  const filteredTemplates = useMemo(() => {
    const query = search.trim().toLowerCase()
    if (!query) return templates
    return templates.filter((template) => {
      const title = template.title.toLowerCase()
      const description = template.description.toLowerCase()
      return title.includes(query) || description.includes(query)
    })
  }, [search, templates])

  const presets = filteredTemplates.filter((template) => template.is_default)
  const saved = filteredTemplates.filter((template) => !template.is_default)

  const handleConfirmDelete = async () => {
    if (!templateToDelete?.id) return
    await deleteTemplate(templateToDelete.id)
    setTemplateToDelete(null)
  }

  return (
    <div className="flex h-[calc(100svh-140px)] flex-col">
      <div className="flex items-center justify-between border-b px-4 py-3">
        <div className="flex items-center gap-3">
          {onBack && (
            <Button size="icon" variant="ghost" onClick={onBack}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
          )}
          <div className="min-w-0">
            <h2 className="text-lg font-semibold">{t("title")}</h2>
            <p className="text-muted-foreground text-xs">{t("description")}</p>
          </div>
        </div>
        <Button onClick={onCreate}>
          <Plus className="mr-2 h-4 w-4" />
          {t("create")}
        </Button>
      </div>

      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        <Input
          placeholder={t("searchPlaceholder", { defaultValue: "Search templates..." })}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />

        {isLoading && (
          <div className="py-8 text-center">
            <Spinner className="text-primary mx-auto size-8" />
          </div>
        )}
        {!isLoading && isError && (
          <div className="space-y-3 py-8 text-center">
            <p className="text-muted-foreground text-sm">
              {t("loadFailed", { defaultValue: "Failed to load style templates." })}
            </p>
            <p className="text-muted-foreground text-xs">{getStyleTemplateErrorMessage(error)}</p>
            <Button variant="outline" onClick={() => void refetch()}>
              {t("retry", { defaultValue: "Retry" })}
            </Button>
          </div>
        )}
        {!isLoading && !isError && (
          <>
            {saved.length > 0 ? (
              <section className="space-y-2">
                <h3 className="text-sm font-medium">{t("picker.saved")}</h3>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {saved.map((template) => (
                    <StyleTemplateCard
                      key={template.id}
                      template={template}
                      onDelete={(nextTemplate) => setTemplateToDelete(nextTemplate)}
                      onEdit={onEdit}
                      onSelect={onEdit}
                    />
                  ))}
                </div>
              </section>
            ) : null}

            {presets.length > 0 ? (
              <section className="space-y-2">
                <h3 className="text-sm font-medium">{t("picker.presets")}</h3>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {presets.map((template) => (
                    <StyleTemplateCard key={template.id} template={template} onSelect={onEdit} />
                  ))}
                </div>
              </section>
            ) : null}

            {presets.length === 0 && saved.length === 0 ? (
              <p className="text-muted-foreground py-8 text-center text-sm">
                {t("noTemplatesDesc")}
              </p>
            ) : null}
          </>
        )}
      </div>

      <AlertDialog
        open={Boolean(templateToDelete)}
        onOpenChange={(open) => !open && setTemplateToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("confirmDelete.title", { defaultValue: "Delete style template" })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("confirmDelete.description", {
                defaultValue: 'Are you sure you want to delete "{{title}}"?',
                title: templateToDelete?.title ?? "",
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t("confirmDelete.cancel", { defaultValue: "Cancel" })}
            </AlertDialogCancel>
            <AlertDialogAction disabled={isDeleting} onClick={() => void handleConfirmDelete()}>
              {t("confirmDelete.delete", { defaultValue: "Delete" })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
